import { listChannels } from '@/features/channels/api';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Loader2, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import {
  ALLOWED_EVENTS,
  type PushEndpoint,
  type PushEvent,
  createEndpoint,
  eventNote,
  isEventAvailable,
  updateEndpoint,
} from './api';

interface Props {
  endpoint?: PushEndpoint;
  onClose: () => void;
}

export function PushEndpointModal({ endpoint, onClose }: Props) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const isEdit = !!endpoint;

  const channelsQ = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  const channels = channelsQ.data ?? [];

  const [name, setName] = useState(endpoint?.name ?? '');
  const [channelId, setChannelId] = useState<number | null>(endpoint?.whatsapp_number_id ?? null);
  const [url, setUrl] = useState(endpoint?.url ?? 'https://');
  const [events, setEvents] = useState<PushEvent[]>(endpoint?.events ?? ['ticket.closed']);
  const [customRows, setCustomRows] = useState<Array<{ key: string; value: string }>>(
    endpoint?.custom_fields && Object.keys(endpoint.custom_fields).length > 0
      ? Object.entries(endpoint.custom_fields).map(([key, value]) => ({ key, value }))
      : [],
  );
  const [error, setError] = useState<string | null>(null);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);

  const toggleEvent = (e: PushEvent) => {
    if (!isEventAvailable(e)) return;
    setEvents((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));
  };

  const addCustomRow = () => setCustomRows((prev) => [...prev, { key: '', value: '' }]);
  const removeCustomRow = (i: number) =>
    setCustomRows((prev) => prev.filter((_, idx) => idx !== i));
  const setCustomRow = (i: number, patch: Partial<{ key: string; value: string }>) =>
    setCustomRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  // Rows → record (ignora chaves vazias; última chave repetida vence).
  const buildCustomFields = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const r of customRows) {
      const k = r.key.trim();
      if (k) out[k] = r.value;
    }
    return out;
  };

  const createMut = useMutation({
    mutationFn: () =>
      createEndpoint({
        name: name.trim(),
        whatsapp_number_id: channelId!,
        url: url.trim(),
        events,
        custom_fields: buildCustomFields(),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['push-endpoints'] });
      setCreatedSecret(res.secret);
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
    },
  });

  const updateMut = useMutation({
    mutationFn: () =>
      updateEndpoint(endpoint!.id, {
        name: name.trim(),
        whatsapp_number_id: channelId!,
        url: url.trim(),
        events,
        custom_fields: buildCustomFields(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['push-endpoints'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
    },
  });

  const submit = () => {
    setError(null);
    if (!name.trim()) return setError('Nome obrigatório');
    if (!channelId) return setError('Selecione um canal');
    if (!url.startsWith('https://')) return setError('URL precisa ser https');
    if (events.length === 0) return setError('Selecione pelo menos um evento');
    if (isEdit) updateMut.mutate();
    else createMut.mutate();
  };

  const isPending = createMut.isPending || updateMut.isPending;

  if (createdSecret) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="w-full max-w-md rounded-md bg-n-0 shadow-xl">
          <header className="border-n-200 border-b px-6 py-4">
            <h2 className="font-bold text-[16px] text-n-900">Webhook criado</h2>
          </header>
          <div className="space-y-3 px-6 py-5 text-[13px]">
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800">
              ⚠️ <b>Copie o secret agora.</b> Ele não poderá ser visto novamente. Use-o pra validar o
              header <code className="font-mono">X-Chatplus-Signature</code> em cada delivery.
            </div>
            <label className="block">
              <span className="mb-1 block font-semibold text-[12px] text-n-700">Secret</span>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={createdSecret}
                  className="h-10 flex-1 rounded border border-n-200 bg-n-50 px-3 font-mono text-[11px]"
                />
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(createdSecret)}
                  className="flex h-10 items-center gap-1 rounded border border-n-200 bg-n-0 px-3 text-[12px] font-semibold hover:bg-n-50"
                  title="Copiar"
                >
                  <Copy size={13} />
                  Copiar
                </button>
              </div>
            </label>
          </div>
          <footer className="flex justify-end gap-2 border-n-200 border-t bg-n-50 px-6 py-4">
            <Button onClick={onClose}>Pronto</Button>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Altura limitada à tela: título e botões fixos, o meio rola (antes o
          cartão passava da tela e cortava em cima e embaixo, sem barra). */}
      <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-md bg-n-0 shadow-xl">
        <header className="flex flex-none items-center justify-between border-n-200 border-b px-6 py-4">
          <h2 className="font-bold text-[16px] text-n-900">
            {isEdit ? 'Editar webhook' : 'Novo webhook'}
          </h2>
          <button type="button" onClick={onClose} className="text-n-500 hover:text-n-700">
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5 text-[13px]">
          <label className="block">
            <span className="mb-1 block font-semibold text-[12px] text-n-700">Nome</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="n8n produção"
            />
          </label>

          <label className="block">
            <span className="mb-1 block font-semibold text-[12px] text-n-700">Canal</span>
            <select
              value={channelId ?? ''}
              onChange={(e) => setChannelId(e.target.value ? Number(e.target.value) : null)}
              className="h-10 w-full rounded-md border border-n-200 bg-n-0 px-3"
            >
              <option value="" disabled>
                {channelsQ.isLoading ? 'Carregando canais…' : 'Selecione um canal'}
              </option>
              {channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName || c.displayNumber || `Canal #${c.id}`} —{' '}
                  {c.channelType === 'meta_official'
                    ? 'Oficial'
                    : c.channelType === 'instagram'
                      ? 'Instagram'
                      : 'QR Code'}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block font-semibold text-[12px] text-n-700">URL</span>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://n8n.exemplo.com/webhook/..."
            />
          </label>

          <div>
            <span className="mb-2 block font-semibold text-[12px] text-n-700">Eventos</span>
            <div className="space-y-2">
              {ALLOWED_EVENTS.map((e) => {
                const available = isEventAvailable(e);
                const note = eventNote(e);
                return (
                  <label
                    key={e}
                    className={`flex items-center gap-2 text-[12px] ${
                      available ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={events.includes(e)}
                      disabled={!available}
                      onChange={() => toggleEvent(e)}
                      className="h-4 w-4"
                    />
                    <code className="font-mono">{e}</code>
                    {note && <span className="text-[11px] text-n-400">{note}</span>}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <span className="mb-1 block font-semibold text-[12px] text-n-700">
              Campos adicionais <span className="font-normal text-n-400">(opcional)</span>
            </span>
            <p className="mb-2 text-[11px] text-n-500">
              Enviados no payload de cada evento, dentro de <code className="font-mono">custom</code>
              . Úteis pra identificar/rotear no seu sistema.
            </p>
            {customRows.length > 0 && (
              <div className="mb-2 space-y-2">
                {customRows.map((row, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: linhas editáveis sem id estável
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={row.key}
                      onChange={(e) => setCustomRow(i, { key: e.target.value })}
                      placeholder="campo"
                    />
                    <Input
                      value={row.value}
                      onChange={(e) => setCustomRow(i, { value: e.target.value })}
                      placeholder="valor"
                    />
                    <button
                      type="button"
                      onClick={() => removeCustomRow(i)}
                      className="flex h-10 w-10 flex-none items-center justify-center rounded border border-n-200 text-n-500 hover:bg-n-50 hover:text-danger-600"
                      aria-label="Remover campo"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={addCustomRow}
              className="flex items-center gap-1 font-semibold text-[12px] text-brand-600 hover:underline"
            >
              <Plus size={13} /> Adicionar campo
            </button>
          </div>

          {error && (
            <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
              {error}
            </div>
          )}
        </div>

        <footer className="flex flex-none justify-end gap-2 border-n-200 border-t bg-n-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-n-200 bg-n-0 px-3 py-2 font-semibold text-[13px]"
          >
            Cancelar
          </button>
          <Button onClick={submit} disabled={isPending}>
            {isPending && <Loader2 className="animate-spin" />}
            {isEdit ? 'Salvar' : 'Criar'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
